import {
	type AppID,
	type AppSettingsValues,
	type DeepPartial,
	type EventListenerMap,
	type NubeComponent,
	type NubeSDK,
	type NubeSDKListenableEvent,
	type NubeSDKListenableSuccessEvent,
	type NubeSDKListener,
	type NubeSDKListenerWithPayload,
	type NubeSDKState,
	type OptionalEventPayload,
	type UISlot,
	WORKER_PRIVATE_STATE_PATHS,
} from "@tiendanube/nube-sdk-internal-types";
import type { NubeBrowserAPIs } from "@tiendanube/nube-sdk-types/src/browser";
import deepcopy from "deepcopy";
import { deepMerge } from "../../../../runtime/src/deep-merge";
import { dispatchMessage, listenMessages } from "../../messages";
import { restoreObjectKeys, saveObjectKeys } from "../../object-keys-utils";
import { wrapEventListener, wrapStateModifier } from "../../telemetry";
import {
	type TransferableConverter,
	createTransferableConverter,
} from "../../transferable-converter";
import { createWorkerIframeMessage } from "./worker-iframe-message";
import { createWorkerNavigate } from "./worker-navigate";
import { createWorkerStorage } from "./worker-storage";
import { type WorkerUISdk, createWorkerUISdk } from "./worker-ui-sdk";

const FULL_REPLACE_PATHS = ["ui.slots.*", "eventPayload"];

// Union type for all possible listeners
type AnyListener = NubeSDKListener | NubeSDKListenerWithPayload;

// Helper function to check if event is a success event
function isSuccessEvent(
	event: NubeSDKListenableEvent,
): event is NubeSDKListenableSuccessEvent {
	return event.endsWith(":success");
}

// Helper function to convert state for listeners with payload
function convertStateForListenerWithPayload(
	state: Readonly<NubeSDKState>,
): Readonly<NubeSDKState> & OptionalEventPayload {
	// Create a compatible state object where eventPayload is optional
	const stateWithOptionalPayload = state as Readonly<NubeSDKState> &
		OptionalEventPayload;
	return stateWithOptionalPayload;
}

interface WorkerSDKOptions {
	customWorkerUISdk?: WorkerUISdk;
}

export function createWorkerSDK(
	initialState: NubeSDKState,
	appId: AppID,
	scriptUrl: string,
	storeId: number,
	storeName: string,
	storeDomain: string,
	options?: WorkerSDKOptions,
	appSettings?: AppSettingsValues,
) {
	const handlers = new Map<NubeSDKListenableEvent, AnyListener[]>();
	const wrappedListeners = new Map<
		NubeSDKListenableEvent,
		Set<{ ref: AnyListener; fn: AnyListener }>
	>();

	let state = deepcopy(initialState);

	let privateState = saveObjectKeys(state, WORKER_PRIVATE_STATE_PATHS);

	let workerUISdk: WorkerUISdk | null = null;

	let browserAPIs: NubeBrowserAPIs | null = null;

	const transferableConverter: TransferableConverter =
		createTransferableConverter();

	const sdk: NubeSDK = {
		on<T extends NubeSDKListenableEvent>(
			event: T,
			listener: EventListenerMap[T],
		) {
			const listeners = handlers.get(event) || [];
			const eventWrappedListeners = wrappedListeners.get(event) || new Set();

			// Wrap the listener with telemetry
			const wrappedListener = wrapEventListener(
				listener as NubeSDKListener,
				appId,
				scriptUrl,
				storeId,
				storeName,
				storeDomain,
			);

			// Store both original and wrapped for proper off() functionality
			eventWrappedListeners.add({
				ref: listener as AnyListener,
				fn: wrappedListener as AnyListener,
			});
			wrappedListeners.set(event, eventWrappedListeners);

			// Add wrapped listener to handlers for execution
			handlers.set(event, [...listeners, wrappedListener as AnyListener]);
		},

		off<T extends NubeSDKListenableEvent>(
			event: T,
			listener: EventListenerMap[T],
		) {
			const listeners = handlers.get(event);
			const eventWrappedListeners = wrappedListeners.get(event);

			if (listeners === undefined || eventWrappedListeners === undefined)
				return;

			// Find the wrapped listener corresponding to the original listener
			let wrappedListenerToRemove: AnyListener | null = null;
			for (const wrapper of eventWrappedListeners) {
				if (wrapper.ref === listener) {
					wrappedListenerToRemove = wrapper.fn;
					eventWrappedListeners.delete(wrapper);
					break;
				}
			}

			// Remove the wrapped listener from handlers
			if (wrappedListenerToRemove) {
				handlers.set(
					event,
					listeners.filter((fn) => fn !== wrappedListenerToRemove),
				);
			}
		},

		send(event, modifier) {
			let newPartialState: DeepPartial<NubeSDKState> | undefined;

			if (modifier) {
				// Wrap the state modifier with telemetry
				const wrappedModifier = wrapStateModifier(
					modifier,
					event,
					appId,
					scriptUrl,
					storeId,
					storeName,
					storeDomain,
				);
				newPartialState = wrappedModifier(state);
			}

			if (modifiesUISlots(newPartialState)) {
				if (!workerUISdk) {
					workerUISdk =
						options?.customWorkerUISdk ?? createWorkerUISdk(sdk, appId);
				}

				newPartialState = applyUISlots(newPartialState, workerUISdk);
			}

			if (newPartialState) {
				// If modifier didn't send eventPayload, set it to null before deepMerge
				if (newPartialState.eventPayload === undefined) {
					newPartialState.eventPayload = null;
				}

				/* For deepMerge, we need to remove eventPayload from the state */
				const { eventPayload, ...rest } = state as NubeSDKState &
					OptionalEventPayload;

				const merged = deepMerge(rest, newPartialState, FULL_REPLACE_PATHS);
				if (merged) {
					/* We need to add newPartialState.eventPayload back to the state */
					state = { ...merged, eventPayload: newPartialState.eventPayload };
					privateState = saveObjectKeys(state, WORKER_PRIVATE_STATE_PATHS);
				}
			}

			dispatchMessage(
				appId,
				event,
				transferableConverter.toTransferable(newPartialState),
			);
		},

		render(slot, component) {
			sdk.send("ui:slot:set", (state) =>
				setUISlot(deepcopy(state), slot, component),
			);
		},

		clearSlot(slot) {
			sdk.send("ui:slot:set", (state) => clearUISlot(deepcopy(state), slot));
		},

		getState() {
			return deepcopy(state);
		},

		getBrowserAPIs() {
			if (browserAPIs === null) {
				browserAPIs = {
					asyncLocalStorage: createWorkerStorage(appId, "local-storage"),
					asyncSessionStorage: createWorkerStorage(appId, "session-storage"),
					navigate: createWorkerNavigate(appId),
					postMessageToIframe: createWorkerIframeMessage(appId),
				};
			}

			return browserAPIs;
		},

		getAppSettings(): AppSettingsValues {
			return appSettings ?? {};
		},
	};

	listenMessages(appId, (event, newState) => {
		if (newState) {
			state = restoreObjectKeys(newState, privateState);
		}

		const eventListeners = handlers.get(event);

		if (eventListeners) {
			for (const fn of eventListeners) {
				if (isSuccessEvent(event)) {
					// For success events, use listeners with payload
					const listenerWithPayload = fn as NubeSDKListenerWithPayload;
					listenerWithPayload(convertStateForListenerWithPayload(state), event);
				} else {
					// For regular events, use regular listeners
					const regularListener = fn as NubeSDKListener;
					regularListener(state, event);
				}
			}
		}
	});

	return Object.freeze(sdk);
}

function modifiesUISlots(newState: DeepPartial<NubeSDKState> | undefined) {
	return newState?.ui?.slots;
}

function setUISlot(
	newState: NubeSDKState,
	slot: UISlot,
	component:
		| NubeComponent
		| NubeComponent[]
		| ((state: Readonly<NubeSDKState>) => NubeComponent | NubeComponent[]),
) {
	const newComponent =
		typeof component === "function" ? component(newState) : component;
	newState.ui = {
		...newState.ui,
		slots: {
			[slot]: newComponent,
		},
	};

	return newState;
}

function clearUISlot(newState: NubeSDKState, slot: UISlot) {
	newState.ui = {
		...newState.ui,
		slots: {
			[slot]: null,
		},
	};

	return newState;
}

function applyUISlots(
	newState: DeepPartial<NubeSDKState> | undefined,
	workerUISdk: WorkerUISdk,
) {
	// If the new state has UI slots, we need to update the UI.
	if (newState?.ui?.slots) {
		const slots = Object.keys(newState?.ui?.slots);
		for (const s of slots) {
			const slot = s as UISlot;
			const component = newState.ui.slots[slot] as NubeComponent | undefined;
			if (component) {
				// The setSlot() function returns a copy of the component with the internal ID set, so we replace
				// the component in the new state with the new one, so the main thread receives the components with
				// their internal id set.
				newState.ui.slots[slot] = workerUISdk.setSlot(slot, component);
			} else {
				workerUISdk.removeSlot(slot);
			}
		}
	}

	return newState;
}
